import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "react-quill/dist/quill.snow.css";
import axiosInstance from "../../../services/api/axios";

import "react-datepicker/dist/react-datepicker.css";

import {
  EditButton,
  GradientButton,
  PrimaryButton,
} from "../../../components/Buttons";

import CostSheetFormModal from "./Forms/CostSheetFormModal";
import { FormModal } from "../../../components/form";
import usePrivilege from "../../../hooks/usePrivilege";
import useSubscriptionFeatures from "../../../hooks/useSubscriptionFeatures";
import { SUB_FEATURES } from "../../../constants/subscriptionFeatures";
import SSPPassphraseModal from "../../../components/SSPPassphraseModal";
import usePagination from "../../../hooks/usePagination";
import PaginationControls from "../../../components/PaginationControls";
import DataTable from "../../../components/DataTable";
import ESignStatusBadge from "../../../components/ESignStatusBadge";
import { getSignatureRequests } from "../../../services/esign";

// Fetches the latest eSign signature request for a (sale, documentType) and
// renders its status badge. Fully guarded — renders nothing if none exists.
const ESignStatus = ({ customerSalesId, documentType }) => {
  const [req, setReq] = useState(null);
  useEffect(() => {
    let active = true;
    if (!customerSalesId) return undefined;
    getSignatureRequests(customerSalesId, documentType)
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.items || [];
        if (!active) return;
        setReq(
          list.length
            ? list.reduce((a, b) =>
                new Date(b.createdDate) > new Date(a.createdDate) ? b : a
              )
            : null
        );
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [customerSalesId, documentType]);
  if (!req) return null;
  return (
    <div className="inline-flex items-center gap-2">
      <ESignStatusBadge status={req.status} />
      {req.status === "COMPLETED" && req.signedPdfUrl && (
        <a
          href={req.signedPdfUrl}
          target="_blank"
          rel="noreferrer"
          className="text-xs font-medium text-flamingo-600 hover:underline"
        >
          View signed
        </a>
      )}
    </div>
  );
};

const PaymentInstallmentsTab = ({
  gridColumns,
  actionColumns,
  sale,
  isHandover,
  setIsHandover,
  filteredcustomerId,
  isCancelled,
  setInstallments,
  refreshNonce,
  setHasUnsavedScheduleInstallmentChanges,

}) => {
  const { hasPrivilege } = usePrivilege();
  const { hasFeature } = useSubscriptionFeatures();
  // BuildVizer Plus: installments table is visible (read-only), but the
  // document-generation surface (Booking / Handover Letter buttons and
  // their underlying flows) drops out. The table itself keeps rendering.
  const canGenerateDocs = hasFeature(SUB_FEATURES.CUSTOMER_DOCUMENTS);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [receiptContent, setReceiptContent] = useState("");
  const [sendLoading, setSendLoading] = useState(false);
  const [currentDocType, setCurrentDocType] = useState(null);
  const [currentDocDetails, setCurrentDocDetails] = useState(null);
  const [costSheetData, setCostSheetData] = useState(null);
  const [, setIsDisabled] = useState(isHandover);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingInstallment, setEditingInstallment] = useState(null);
  const [initialEditSnapshot, setInitialEditSnapshot] = useState(null);
  const [editedDetails, setEditedDetails] = useState("");
  const [editedReceivedAmount, setEditedReceivedAmount] = useState("");
  const [editedReceivedDate, setEditedReceivedDate] = useState(""); // Store the date as a string
  const [mseb, setMseb] = useState("");
  const [infraCharges, setInfraCharges] = useState("");
  const [societyFormationCharges, setSocietyFormationCharges] = useState("");
  const [stampDutyParking, setStampDutyParking] = useState("");
  const [legalCharges, setLegalCharges] = useState("");
  const [grossGovernmentCharges, setGrossGovernmentCharges] = useState("");
  const [totalB, setTotalB] = useState("");
  const [totalC, setTotalC] = useState("");

  const [grandTotal, setGrandTotal] = useState("");
  const [costSheetTerms, setCostSheetTerms] = useState(costSheetData?.cost_sheet_terms || "");
  const [showSSPModal, setShowSSPModal] = useState(false);
  const [sspLoading, setSspLoading] = useState(false);
  const [, setPendingSend] = useState(false); // Track if send is pending passphrase

  // Create fetch function for usePagination hook
  const fetchInstallmentsBackend = useCallback(async ({ page, page_size, search }) => {
    if (!sale?.customerSalesId) {
      return { items: [], total: 0, page: 1, page_size: page_size, total_pages: 1, has_next: false, has_previous: false };
    }

    const params = {
      page: page,
      page_size: page_size,
      customerSalesId: sale.customerSalesId
    };

    // Add search parameter if provided
    if (search) {
      params.search = search;
    }

    const response = await axiosInstance.get('/customer/sales/installment', {
      params: params,
    });

    // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
    if (response?.data?.items !== undefined && response?.data?.total !== undefined) {
      return response.data;
    } else if (response?.items !== undefined && response?.total !== undefined) {
      return response;
    } else if (Array.isArray(response?.data)) {
      // If it's an array, wrap it in paginated format for backward compatibility
      return {
        items: response.data,
        total: response.data.length,
        page: page,
        page_size: page_size,
        total_pages: Math.ceil(response.data.length / page_size),
        has_next: page * page_size < response.data.length,
        has_previous: page > 1
      };
    } else if (Array.isArray(response)) {
      // If it's an array directly, wrap it in paginated format
      return {
        items: response,
        total: response.length,
        page: page,
        page_size: page_size,
        total_pages: Math.ceil(response.length / page_size),
        has_next: page * page_size < response.length,
        has_previous: page > 1
      };
    }
    return response?.data || response;
  }, [sale?.customerSalesId]);

  // Use pagination hook with manual search mode
  const {
    data: paginationData,
    pagination,
    goToPage,
    changePageSize,
    refetch
  } = usePagination(fetchInstallmentsBackend, 30, '', true, false);

  // Extract and process installments from paginated response
  const installments = useMemo(() => {
    if (!paginationData) return [];
    
    // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
    let installmentsArray = [];
    if (paginationData?.items && Array.isArray(paginationData.items)) {
      installmentsArray = paginationData.items;
    } else if (Array.isArray(paginationData)) {
      installmentsArray = paginationData;
    }

    // Process installments to add isRowDisabled flag
    return installmentsArray.map((item) => ({
      ...item,
      isRowDisabled: item.isGeneratedSent === true || sale?.propertyData?.isHandover === true || sale?.cancellationStatus === "Initiated",
      paymentType: item.paymentType ? item.paymentType : {},
      customerSales: {
        customerSalesId: sale?.customerSalesId,
      },
    }));
  }, [paginationData, sale]);

  // Refetch installments when sale.customerSalesId changes
  useEffect(() => {
    if (sale?.customerSalesId) {
      refetch();
    }
  }, [sale?.customerSalesId, refetch]);

  // Refetch when the parent signals a change (e.g. a receipt was sent from the
  // parent's flow). Keeps the grid in sync without a manual refresh.
  useEffect(() => {
    if (refreshNonce) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNonce]);

  // Update parent component's installments state when installments change
  useEffect(() => {
    if (setInstallments) {
      setInstallments(installments);
    }
  }, [installments, setInstallments]);

  useEffect(() => {
    setIsDisabled(isHandover);
  }, [isHandover]);

  // Remove SSP modal logic from handleGenerateDocument
  const handleGenerateDocument = async (docType) => {
    // Directly open the preview modal, no SSP modal
    setCurrentDocType(docType);
    let notificationType;
    switch (docType) {
      case "Booking Letter":
        notificationType = "BOOKING_LETTER";
        break;
      case "Cancellation Letter":
        notificationType = "CANCELLATION_LETTER";
        break;
      case "Handover Letter":
        notificationType = "PROPERTY_HANDOVER_LETTER";
        break;
      case "Cost Sheet":
        notificationType = "COST_SHEET_NOTIFICATION";
        break;
      default:
        notificationType = "BOOKING_LETTER";
    }
    try {
      const response = await axiosInstance.post("/generate/receipt-preview", {
        notificationType: notificationType,
        customerSalesId: sale.customerSalesId,
      });
      setCurrentDocDetails({
        notificationType: notificationType,
        customerSalesId: sale.customerSalesId,
        content: response,
      });
      if (docType === "Cost Sheet") {
        const parsedData = JSON.parse(response);
        setCostSheetData(parsedData);
      } else {
        setReceiptContent(response);
      }
      setModalIsOpen(true);
    } catch (error) {
      toast.error(error.message || `Failed to generate ${docType}`);
    }
  };

  // Move SSP modal logic to handleSendDocument
  const handleSendDocument = async (updatedContent) => {
    setPendingSend(true);
    setShowSSPModal(true);
  };

  // Helper to actually send after passphrase
  const actuallySendDocument = async (updatedContent, passphrase) => {
    setSendLoading(true);
    try {
      let payload = {};
      if (!currentDocDetails) {
        toast.error("No document details available to send.");
        setSendLoading(false);
        return; // Prevents success toast if no data
      }
      if (currentDocType === "Cost Sheet") {
        payload = {
          ...currentDocDetails,
          content: updatedContent,
          passphrase: passphrase,
        };
      } else {
        payload = {
          ...currentDocDetails,
          passphrase: passphrase,
        };
      }
      await axiosInstance.post("/generate/send/receipt-notification", payload);
      //toast.success(`${currentDocType} sent successfully`);
      setIsHandover(true);
      setIsDisabled(true);
      // Refresh the grid so any server-side state change (e.g. handover /
      // sent status) shows immediately without a manual page refresh.
      await refetch();
      closeModal();
    } catch (error) {
      // Handle specific SSL certificate error
      if (error.response?.status === 400 && error.response?.data?.detail === "SSL certificate not generated for the customer") {
        toast.error("SSL certificate not generated for the customer. Please contact support.");
      } else if (error.response?.status === 402 && error.response?.data?.detail === "Invalid passphrase for private key") {
        toast.error("Invalid passphrase for private key. Please check your SSL passphrase and try again.");
      } else {
        toast.error(error.response?.data?.message || error.message || `Failed to send ${currentDocType}`);
      }
    } finally {
      setSendLoading(false);
    }
  };

  const handleSSPConfirm = async (passphrase) => {
    setSspLoading(true);
    try {
      
      // After passphrase, send the document
      await actuallySendDocument(receiptContent, passphrase);
      setShowSSPModal(false);
      setPendingSend(false);
    } catch (error) {
      // Handle specific SSL certificate error
      if (error.response?.status === 400 && error.response?.data?.detail === "SSL certificate not generated for the customer") {
        toast.error("SSL certificate not generated for the customer. Please contact support.");
      } else if (error.response?.status === 402 && error.response?.data?.detail === "Invalid passphrase for private key") {
        toast.error("Invalid passphrase for private key. Please check your SSL passphrase and try again.");
      } else {
        toast.error(error.response?.data?.message || error.message || "Invalid passphrase or failed to send document");
      }
    } finally {
      setSspLoading(false);
    }
  };

  const handleSSPClose = () => {
    setShowSSPModal(false);
    setPendingSend(false);
    setSspLoading(false);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setReceiptContent("");
    setCostSheetData(null);  // Clear Cost Sheet Data
    setCurrentDocType(null);
    setCurrentDocDetails(null);
  };

  // modalStyle / containerStyle removed — superseded by FormModal + DataTable.

  const handleEditInstallment = (installment) => {
    setEditingInstallment(installment);
    const details = installment?.details ?? "";
    const receivedAmount =
      installment?.receivedAmount !== undefined && installment?.receivedAmount !== null
        ? String(installment.receivedAmount)
        : "";
    const receivedDate = installment?.receivedDate ? installment.receivedDate.split('T')[0] : "";
    setEditedDetails(details);
    setEditedReceivedAmount(receivedAmount);
    // Initialize with the receivedDate as a string, if it exists
    setEditedReceivedDate(receivedDate);
    setInitialEditSnapshot({
      details,
      receivedAmount,
      receivedDate,
    });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingInstallment(null);
    setInitialEditSnapshot(null);
    setEditedDetails("");
    setEditedReceivedAmount("");
    setEditedReceivedDate("");
  };

  const handleSaveInstallment = async () => {
    if (!editingInstallment) return;

    // A payment can't be received in the future.
    if (editedReceivedDate) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (new Date(editedReceivedDate) > today) {
        toast.error("Received date cannot be in the future.");
        return;
      }
    }

    // Function to convert a local date string to UTC date string
    const convertToUTCDateString = (dateString) => {
      if (!dateString) return null;

      const localDate = new Date(dateString);
      const utcDate = new Date(localDate.getTime() - (localDate.getTimezoneOffset() * 60000));
      return utcDate.toISOString().split("T")[0];
    };
    console.log("installment",editingInstallment);

    const payload = {
      customerSalesId: editingInstallment.customerSales.customerSalesId,
      projectedAmount: parseFloat(editingInstallment.projectedAmount),
      receivedAmount: parseFloat(editedReceivedAmount),
      projectedDate: editingInstallment.projectedDate
        ? new Date(editingInstallment.projectedDate).toISOString().split("T")[0]
        : null,
      // Use editedReceivedDate directly as it's already in the YYYY-MM-DD format
      receivedDate: convertToUTCDateString(editedReceivedDate),
      details: editedDetails,
      paymentTypeId: editingInstallment.paymentType?.paymentTypeId || null,
      statusTypeId:
        editingInstallment.statusType?.statusType?.statusTypeId ||
     
        null,
    };

    try {
      await axiosInstance.put(`/customer/sales/installment`, payload, {
        params: {
          customer_sales_installment_id:
            editingInstallment.customerSalesInstallmentId,
        },
      });

      toast.success("Installment updated successfully");
      // Refetch installments to get updated data
      await refetch();
      if (setHasUnsavedScheduleInstallmentChanges) {
        setHasUnsavedScheduleInstallmentChanges(false);
      }
      closeEditModal();
    } catch (error) {
      console.error("Error updating installment:", error);
      toast.error(error.message || "Failed to update installment");
    }
  };

  useEffect(() => {
    if (!setHasUnsavedScheduleInstallmentChanges) return;
    const isScheduledPayment = editingInstallment?.paymentType?.type === "SCHEDULED_PAYMENT";
    const isDirty =
      Boolean(initialEditSnapshot) &&
      (
        (editedDetails ?? "") !== (initialEditSnapshot?.details ?? "") ||
        String(editedReceivedAmount ?? "") !== String(initialEditSnapshot?.receivedAmount ?? "") ||
        (editedReceivedDate ?? "") !== (initialEditSnapshot?.receivedDate ?? "")
      );

    setHasUnsavedScheduleInstallmentChanges(Boolean(editModalOpen && isScheduledPayment && isDirty));
  }, [
    editModalOpen,
    editingInstallment,
    editedDetails,
    editedReceivedAmount,
    editedReceivedDate,
    initialEditSnapshot,
    setHasUnsavedScheduleInstallmentChanges,
  ]);

  const generatePaymentType = useCallback((params) => {
    if (!params.data || !params.data.paymentType) {
      return "N/A";
    }

    const paymentType = params.data.paymentType?.type;
    // v2: wbsStage embed replaces statusType (§D); fall back to statusType for backwards compat
    const stageEmbed = params.data.wbsStage || params.data.statusType?.statusType || params.data.statusType;
    const stageName = stageEmbed?.typeName;
    const stageTarget = params.data.floorTarget ?? params.data.statusType?.target;

    // If the type is SLAB_WISE_PAYMENT and a stage name exists
    if (paymentType === "SLAB_WISE_PAYMENT" && stageName) {
      if (stageName === "SLAB") {
        // If stageName is SLAB, return "SLAB - floorTarget"
        return `${stageName} - ${stageTarget ?? ""}`;
      } else if (stageName === "CUSTOM" && (params.data.wbsStage?.particulars || params.data.statusType?.particulars)) {
        return params.data.wbsStage?.particulars || params.data.statusType?.particulars;
      } else {
        return stageEmbed?.label || stageName;
      }
    }

    if (paymentType === "SCHEDULED_PAYMENT") {
      const fullPaymentInstallments = installments.filter(
        (row) => row.paymentType?.type === "SCHEDULED_PAYMENT"
      );

      const index = fullPaymentInstallments.findIndex(
        (installment) =>
          installment.customerSalesInstallmentId ===
          params.data.customerSalesInstallmentId
      );

      return index !== -1 ? `Installment ${index + 1}` : "N/A";
    }

    return paymentType || "N/A";
  }, [installments]);

  const generateActionButtons = (params) => {
    const isDisabled = params.data.isRowDisabled;

    return (
      <div className="flex justify-center items-center h-full  gap-2">
        <EditButton onClick={() => handleEditInstallment(params.data)} disabled={isDisabled} />
        {/* <Button
          size="sm"
          variant="outline"
          onClick={() => handleEditInstallment(params.data)}
          color="black"
          className="p-2"
          disabled={isDisabled} // Disable the edit button
        >
          <Edit size={17.5} />
        </Button> */}
      </div>
    );
  };

  const updatedActionColumns = useMemo(
    () =>
      actionColumns.map((col) => ({
        ...col,
        cellRenderer: generateActionButtons,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actionColumns]
  );

  const updatedGridColumns = useMemo(() => {
    return gridColumns.map((col) => {
      if (col.field === "paymentType.type") {
        return {
          ...col,
          headerName: "Payment Type",
          cellRenderer: generatePaymentType,
        };
      }
      return col;
    });
  }, [gridColumns, generatePaymentType]);

  // AG-Grid → DataTable adapter (same shape used by the LeadsDashboard
  // migration). Converts {headerName, field, minWidth, cellRenderer,
  // valueFormatter} into the DataTable column spec and synthesizes a
  // params object so existing cellRenderers continue to work as-is.
  const dotGet = (obj, path) =>
    String(path || "").split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
  const dtColumns = useMemo(() => {
    const cols = [...updatedGridColumns, ...updatedActionColumns];
    return cols.map((col, idx) => {
      const width = col.maxWidth || col.minWidth || col.width || 150;
      return {
        key: col.field || col.headerName || `col-${idx}`,
        header: col.headerName || "",
        width: Number(width),
        cellClassName: "align-middle",
        render: (row) => {
          const value = col.field ? dotGet(row, col.field) : undefined;
          const params = { data: row, value };
          if (typeof col.cellRenderer === "function") {
            const out = col.cellRenderer(params);
            return out == null ? <span className="text-slate-400">—</span> : out;
          }
          if (typeof col.valueFormatter === "function") {
            const formatted = col.valueFormatter(params);
            return formatted == null || formatted === ""
              ? <span className="text-slate-400">—</span>
              : <span className="text-sm text-slate-700">{formatted}</span>;
          }
          if (value == null || value === "") return <span className="text-slate-400">—</span>;
          return <span className="text-sm text-slate-700 truncate block" title={String(value)}>{String(value)}</span>;
        },
      };
    });
  }, [updatedGridColumns, updatedActionColumns]);

  return (
    // Single wrapper — the parent (SoldPropertyDetails) already provides
    // the card chrome, so we don't re-wrap in another bg-white shadow card.
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base font-semibold text-slate-900">Payment installments</h2>
        {hasPrivilege("canEdit") && canGenerateDocs && (
          <div className="flex flex-wrap gap-4 items-start">
            <div className="flex flex-col items-start gap-1.5">
              <GradientButton
                onClick={() => handleGenerateDocument("Booking Letter")}
                disabled={sale?.propertyData?.isHandover || sale?.cancellationStatus === "Initiated"}
              >
                Booking Letter
              </GradientButton>
              <ESignStatus customerSalesId={sale?.customerSalesId} documentType="BOOKING_LETTER" />
            </div>
            <div className="flex flex-col items-start gap-1.5">
              <GradientButton
                onClick={() => handleGenerateDocument("Handover Letter")}
                disabled={sale?.cancellationStatus === "Initiated" || !sale?.propertyData?.project?.isOccupancyDone}
              >
                Handover Letter
              </GradientButton>
              <ESignStatus customerSalesId={sale?.customerSalesId} documentType="PROPERTY_HANDOVER_LETTER" />
            </div>
          </div>
        )}
      </div>

      <DataTable
        columns={dtColumns}
        data={installments}
        minWidth={hasPrivilege("canEdit") ? 1300 : 1120}
        rowKey={(r, i) => r?.installmentId ?? r?.id ?? i}
        emptyState={
          <div className="px-6 py-12 text-center text-sm text-slate-500">
            No installments recorded yet.
          </div>
        }
      />

      {pagination && pagination.total > 0 && (
        <PaginationControls
          pagination={pagination}
          onPageChange={goToPage}
          onPageSizeChange={changePageSize}
          itemName="installments"
          variant="list"
        />
      )}

      {/* Cost Sheet / document preview modal — single shell, body branches
          on currentDocType (cost-sheet wizard vs document preview iframe). */}
      <FormModal
        open={modalIsOpen}
        onClose={closeModal}
        title={currentDocType || ""}
        size="2xl"
      >
            <div className="flex flex-col h-full">
              {currentDocType === "Cost Sheet" ? (
                <CostSheetFormModal
                  costSheetData={costSheetData}
                  onClose={closeModal}
                  onSendDocument={handleSendDocument}
                  //This is how we pass loading to the funtion

                  setSendLoading={setSendLoading}
                  mseb={mseb}
                  setMseb={setMseb}
                  infraCharges={infraCharges}
                  setInfraCharges={setInfraCharges}
                  societyFormationCharges={societyFormationCharges}
                  setSocietyFormationCharges={setSocietyFormationCharges}
                  stampDutyParking={stampDutyParking}
                  setStampDutyParking={setStampDutyParking}
                  legalCharges={legalCharges}
                  setLegalCharges={setLegalCharges}
                  grossGovernmentCharges={grossGovernmentCharges}
                  setGrossGovernmentCharges={setGrossGovernmentCharges}
                  totalB={totalB}
                  setTotalB={setTotalB}
                  totalC={totalC}
                  setTotalC={setTotalC}
                  grandTotal={grandTotal}
                  setGrandTotal={setGrandTotal}
                  costSheetTerms={costSheetTerms}
                  setCostSheetTerms={setCostSheetTerms}
                />
              ) : (
                <div className="flex flex-col flex-1 h-full">
                  {currentDocType === "Handover Letter" || currentDocType === "Booking Letter" ? (
                    
                    <div className="border rounded-lg bg-white flex flex-col items-center relative w-full flex-1 overflow-hidden">
                      <div className="flex-1 overflow-auto p-6 w-full flex justify-center h-full">
                      
                          <div
                            className="handover-html-preview"
                            style={{
                              width: "794px",
                              minHeight: "1123px",
                              border: "none",
                              background: "white",
                              margin: "20px 0",
                              transform: "scale(0.5)",
                              transformOrigin: "top center",
                            }}
                            dangerouslySetInnerHTML={{ __html: receiptContent }}
                          />
                        </div>
                      
                    </div>
                  ) : (
                    // Default layout for other documents (Booking Letter, etc.)
                    <div className="border rounded-lg bg-white flex flex-col items-center relative w-full flex-1 overflow-hidden">
                      <div className="flex-1 overflow-auto p-4 w-full flex justify-center">
                        <iframe
                          title="Document Preview"
                          srcDoc={receiptContent}
                          style={{
                            width: "100%",
                            maxWidth: "1000px",
                            height: "100%",
                            minHeight: "600px",
                            border: "none",
                            background: "white",
                            transform: "scale(0.5)",
                        transformOrigin: "top center",
                          }}
                        />
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end gap-4 mt-4 pt-4 border-t bg-white sticky bottom-0 z-10">

                    <PrimaryButton
                      className="w-32"
                      onClick={handleSendDocument}
                      disabled={sendLoading}
                    >
                      {sendLoading ? "Sending..." : "Send"}
                    </PrimaryButton>
                  </div>
                </div>
              )}
            </div>
      </FormModal>
      {/* Edit Installment Modal — bespoke form (mix of read-only details +
          three editable fields). Wrapped in FormModal for shell consistency;
          internal layout left as-is to avoid risk on the payment flow. */}
      <FormModal
        open={editModalOpen}
        onClose={closeEditModal}
        title="Edit Installment"
        size="xl"
      >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Payment Type:
                </label>
                <div className="py-2 px-3 text-gray-700">
                  {editingInstallment?.paymentType?.type || "N/A"}
                </div>
              </div>
              <div>
                <label className="block text-gray-700  font-medium mb-2">
                  Projected Amount:
                </label>
                <div className="py-2 px-3 text-gray-700">
                  {editingInstallment?.projectedAmount || "N/A"}
                </div>
              </div>
              <div>
                <label className="block text-gray-700 text-md font-medium mb-2">
                  Projected Date:
                </label>
                <div className="py-2 px-3 text-gray-700">
                  {editingInstallment?.projectedDate
                    ? new Date(
                      editingInstallment?.projectedDate
                    ).toLocaleDateString()
                    : "N/A"}
                </div>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-gray-700  font-medium mb-2">
                Details (Cheque No, NEFT/IMPS/RTGS transaction ID)
              </label>
              <input
                type="text"
                value={editedDetails}
                onChange={(e) => setEditedDetails(e.target.value)}
                className="border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="mb-4 flex-1">
                <label className="block text-gray-700  font-medium mb-2">
                  Received Amount
                </label>
                <input
                  type="number"
                  value={editedReceivedAmount}
                  onChange={(e) => setEditedReceivedAmount(e.target.value)}
                  className="  border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
              <div className="mb-4 flex-1">
                <label className="block text-gray-700 font-medium mb-2">
                  Received Date
                </label>
                <input
                  type="date"
                  value={editedReceivedDate}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setEditedReceivedDate(e.target.value)}
                  className="border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                />
              </div>
            </div>
            <div className="flex justify-end gap-4 mt-6">
              <PrimaryButton
                className={"w-32"}
                onClick={handleSaveInstallment}
                disabled={sendLoading}
              >
                {sendLoading ? "Saving..." : "Save"}
              </PrimaryButton>
            </div>
      </FormModal>

      {/* SSP Passphrase Modal */}
      <SSPPassphraseModal
        isOpen={showSSPModal}
        onClose={handleSSPClose}
        onConfirm={handleSSPConfirm}
        loading={sspLoading}
      />
    </div>
  );
};

export default PaymentInstallmentsTab;